<template>
  <v-container>
    <div class="elevation-2 px-5 mt-3">
      <SimplePageHeader title="Document Templates" class="px-4 pr-5 mb-5">
        <template v-slot:right-buttons>
          <ButtonCustomIcon
            icon="add"
            color="primary"
            text="Add"
            :routeName="documentTemplateCreateRoute.name"
            :block="true"
          />
        </template>
      </SimplePageHeader>
      <v-row class="px-5 pb-2" align="center">
        <v-col cols="12" sm="3" class="pb-0">
          <v-text-field
            v-model="filterName"
            label="Name"
            clearable
            outlined
          />
        </v-col>
        <v-col cols="12" sm="3" class="pb-0">
          <v-select
            v-model="filterDocumentTypeId"
            :items="documentTypeList"
            item-text="name"
            item-value="id"
            label="Document Type"
            clearable
            outlined
          />
        </v-col>
        <v-col cols="12" sm="3" class="pb-0">
          <BusinessEntitiesDropDownList
            v-model="filterBusinessEntityId"
            :clearable="true"
            @businessEntityIdChange="handleBusinessEntityChange"
          />
        </v-col>
        <v-col cols="12" sm="3" class="pb-0">
          <ClientsDropDownList
            :id="filterClientId"
            :businessEntityId="filterBusinessEntityId"
            :clearable="true"
            @clientIdChange="filterClientId = $event"
          />
        </v-col>
        <v-col cols="12" sm="3" class="pb-0">
          <v-switch
            v-model="filterIsActive"
            :label="filterIsActive ? 'Active' : 'Inactive'"
            class="mt-0"
            hide-details
            @change="loadTemplates"
          />
        </v-col>
      </v-row>
      <v-row justify="end">
        <v-col cols="12" md="6">
          <FilterButtons
            @search="loadTemplates"
            @clear="handleClearClick"
          ></FilterButtons>
        </v-col>
      </v-row>
      <FilterButtonsSmall
        @search="loadTemplates"
        @clear="handleClearClick"
      ></FilterButtonsSmall>
    </div>
    <v-container fluid class="px-0 pt-2 mt-4">
      <v-layout column>
        <v-data-table
          dense
          :headers="headers"
          :items="documentTemplateList"
          class="elevation-2"
        >
          <template v-slot:item.isActive="{ item }">
            <v-simple-checkbox
              v-model="item.isActive"
              disabled
            />
          </template>
          <template v-slot:item.action="{ item }">
            <v-btn
              class="mx-1 my-1"
              color="primary"
              outlined
              @click="editTemplate(item)"
            >
              <v-icon>edit</v-icon>
            </v-btn>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-1 my-1"
                  color="primary"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  @click="copyTemplate(item)"
                >
                  <v-icon>content_copy</v-icon>
                </v-btn>
              </template>
              <span>Copy Template</span>
            </v-tooltip>
            <v-tooltip v-if="item.isActive" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  class="mx-1 my-1"
                  color="red"
                  outlined
                  v-bind="attrs"
                  v-on="on"
                  @click="deactivateTemplate(item)"
                >
                  <v-icon>block</v-icon>
                </v-btn>
              </template>
              <span>Deactivate</span>
            </v-tooltip>
          </template>
        </v-data-table>
      </v-layout>
    </v-container>
    <ConfirmationModal
      title="Deactivate Template"
      :contentText="templateToDeactivate ? `Deactivate template &quot;${templateToDeactivate.name}&quot;? It will no longer appear in active templates.` : ''"
      :showModal="showDeactivateConfirmation"
      toolbarColor="primary"
      okText="Deactivate"
      @ok-click="handleDeactivateConfirmed"
      @cancel-click="showDeactivateConfirmation = false; templateToDeactivate = null"
    />
    <CopyTemplateDialog
      v-if="showCopyDialog"
      :template="selectedTemplate"
      @close="showCopyDialog = false"
      @copied="handleCopied"
    />
    <BottomSpacer />
  </v-container>
</template>

<script>
import { mapActions } from 'vuex'
import { mapFields } from '@/vueHelpers/vuex'
import { documentTemplateCreateRoute, documentTemplateEditRoute } from '@/lib/constants/routes'
import { ButtonCustomIcon, BottomSpacer, FilterButtons, FilterButtonsSmall } from '@/components/buttons'
import { SimplePageHeader } from '@/components/pageComponents'
import { BusinessEntitiesDropDownList } from '@/components/businessEntities'
import { ClientsDropDownList } from '@/components/clients'
import CopyTemplateDialog from './CopyTemplateDialog'
import { ConfirmationModal } from '@/components/dialogs'
import api from '@/lib/api'
import { PATH_DOCUMENT_TEMPLATES } from '@/lib/constants/api'
import { notifySuccess, notifyError } from '@/lib/notifications'

export default {
  components: {
    ButtonCustomIcon,
    BottomSpacer,
    SimplePageHeader,
    BusinessEntitiesDropDownList,
    ClientsDropDownList,
    CopyTemplateDialog,
    ConfirmationModal,
    FilterButtons,
    FilterButtonsSmall
  },
  data () {
    return {
      documentTemplateCreateRoute,
      filterName: null,
      filterDocumentTypeId: null,
      filterBusinessEntityId: null,
      filterClientId: null,
      filterIsActive: true,
      showCopyDialog: false,
      selectedTemplate: null,
      showDeactivateConfirmation: false,
      templateToDeactivate: null,
      headers: [
        { text: 'Name', value: 'name', sortable: true },
        { text: 'Document Type', value: 'documentTypeName', sortable: true, width: '20%' },
        { text: 'Client', value: 'clientName', sortable: true, width: '25%' },
        { text: 'Active', value: 'isActive', align: 'center', sortable: false, width: '10%' },
        { text: 'Actions', value: 'action', align: 'center', sortable: false, width: '15%' }
      ]
    }
  },
  computed: {
    ...mapFields('documentTemplates', ['documentTemplateList']),
    ...mapFields('documentTypes', ['documentTypeList'])
  },
  methods: {
    ...mapActions('documentTemplates', ['getDocumentTemplates']),
    ...mapActions('documentTypes', ['getDocumentTypes']),
    loadTemplates () {
      const filters = { isActive: this.filterIsActive }
      if (this.filterName) filters.name = this.filterName
      if (this.filterDocumentTypeId) filters.documentTypeId = this.filterDocumentTypeId
      if (this.filterBusinessEntityId) filters.businessEntityId = this.filterBusinessEntityId
      if (this.filterClientId) filters.clientId = this.filterClientId
      this.getDocumentTemplates(filters)
    },
    handleBusinessEntityChange () {
      this.filterClientId = null
    },
    editTemplate (template) {
      this.$router.push({ name: documentTemplateEditRoute.name, params: { id: template.id } })
    },
    copyTemplate (template) {
      this.selectedTemplate = template
      this.showCopyDialog = true
    },
    handleCopied (newName) {
      this.showCopyDialog = false
      api.doPost({ path: `${PATH_DOCUMENT_TEMPLATES}/${this.selectedTemplate.id}/copy`, data: { name: newName, clientId: this.selectedTemplate.clientId } })
        .then(response => {
          if (!response.data) {
            notifyError(this.$store.dispatch, response.errorMessage || 'Unable to copy template')
            return
          }
          notifySuccess(this.$store.dispatch, 'Template copied')
          this.loadTemplates()
        })
        .catch(() => notifyError(this.$store.dispatch, 'Unable to copy template'))
    },
    deactivateTemplate (template) {
      this.templateToDeactivate = template
      this.showDeactivateConfirmation = true
    },
    handleDeactivateConfirmed () {
      this.showDeactivateConfirmation = false
      const updated = { ...this.templateToDeactivate, isActive: false }
      this.templateToDeactivate = null
      api.doPut({ path: PATH_DOCUMENT_TEMPLATES, data: updated })
        .then(response => {
          if (!response.data) {
            notifyError(this.$store.dispatch, 'Unable to deactivate template')
            return
          }
          notifySuccess(this.$store.dispatch, 'Template deactivated')
          this.loadTemplates()
        })
        .catch(() => notifyError(this.$store.dispatch, 'Unable to deactivate template'))
    },
    handleClearClick () {
      this.filterName = null
      this.filterDocumentTypeId = null
      this.filterBusinessEntityId = null
      this.filterClientId = null
      this.filterIsActive = true
      this.loadTemplates()
    }
  },
  created () {
    this.getDocumentTypes()
    this.loadTemplates()
  }
}
</script>
